// JWT 유틸리티 re-export
export { isTokenValid, getUserIdFromToken, getTokenExpiration } from './jwt'

import { ensureFcmTokenUpdated, removeFcmToken } from './fcmTokenManager'
import { getUserIdFromToken } from './jwt'
import { SESSION_STORAGE_KEYS, safeSessionStorage } from '@/constants/storageKeys'
import { logger } from './logger'
import { useAuthStore } from '@/store/authStore'

import { getCheckTokenApi } from '@/api/authApi'
import { useUserStore } from '@/store/userStore'
import { useAppStore } from '@/store/appStore'

/**
 * 사용자 ID를 가져오는 함수 (토큰 또는 localStorage에서)
 */
export const getUserId = (token?: string): number | null => {
  // 1. 토큰에서 user_id 추출 시도
  if (token) {
    const userIdFromToken = getUserIdFromToken(token)
    if (userIdFromToken) {
      return userIdFromToken
    }
  }

  // 2. Zustand store에서 user_id 가져오기
  try {
    const userId = useUserStore.getState().userInfo.user_id
    if (userId && userId !== 0) {
      return userId
    }
  } catch (error) {
    logger.error('Zustand store에서 user_id 추출 실패', error)
  }

  return null
}

export interface AuthCheckResult {
  authenticated: boolean
  needsRedirect: boolean
  redirectPath?: string
  step: 'access_token_valid' | 'access_token_invalid' | 'no_access_token' | 'signup_required'
  message?: string
}

/**
 * 새로고침 시 인증 상태 확인 로직 (메모리-only 방식)
 * 1. 메모리에서 액세스 토큰 존재 확인 -> 없으면 로그인 화면
 * 2. 액세스 토큰 유효성 확인 -> 유효하면 API로 추가 확인
 * 3. API 확인 통과하면 사용자 정보 확인 (회원가입 완료 여부)
 * 4. is_agree가 false면 회원가입 화면으로 리다이렉트
 * 5. 토큰이 유효하지 않으면 Axios 인터셉터가 자동으로 네이티브에 갱신 요청
 */
export const checkAuthStatus = async (): Promise<AuthCheckResult> => {
  logger.auth('인증 상태 확인 시작')

  // 1. 메모리에서 액세스 토큰 조회
  const accessToken = useAuthStore.getState().getAccessToken()

  if (!accessToken) {
    logger.auth('Access Token 없음')
    return {
      authenticated: false,
      needsRedirect: true,
      step: 'no_access_token',
      message: '로그인이 필요합니다.'
    }
  }

  // 2. Access Token이 있으면 API로 검증
  try {
    const checkResult = await getCheckTokenApi()
    if (checkResult.code === '200') {
      // 3. 사용자 정보 확인
      const userInfo = useAuthStore.getState().userInfo

      // 나중에 수정 필요 (구글로그인 시 취소 눌러도 이쪽으로 이동됨)
      if (userInfo && userInfo.is_agree === false) {
        logger.auth('회원가입 미완료 - TOS 페이지로 이동')
        return {
          authenticated: false,
          needsRedirect: true,
          redirectPath: `/auth/tos?user_id=${userInfo.user_id}`,
          step: 'signup_required',
          message: '회원가입을 완료해주세요.'
        }
      }

      logger.success('인증 상태 유효')

      // 웹뷰 환경에서 FCM 토큰 확인 (비동기로 실행하여 인증 플로우 블로킹하지 않음)
      if (userInfo) {
        setTimeout(() => {
          ensureFcmTokenUpdated(userInfo.user_id).catch(error => {
            logger.warn('FCM 토큰 확인 중 오류', error)
          })
        }, 2000)
      }

      return {
        authenticated: true,
        needsRedirect: false,
        step: 'access_token_valid',
        message: 'Access Token이 유효합니다.'
      }
    }
  } catch (error) {
    logger.warn('[Auth] API 확인 중 오류 발생', error)
  }

  // 4. 토큰이 유효하지 않으면 (Axios 인터셉터가 자동으로 네이티브에 갱신 요청함)
  logger.auth('Access Token 유효하지 않음')
  return {
    authenticated: false,
    needsRedirect: true,
    step: 'access_token_invalid',
    message: '토큰이 유효하지 않습니다.'
  }
}

/**
 * 로그아웃 처리 (메모리-only 방식)
 * - 메모리 인증 정보를 삭제하고 로그인 페이지로 이동
 * - 네이티브에 로그아웃 요청
 * - 패스코드도 함께 초기화
 * - FCM 토큰도 서버에서 삭제
 */
export const logout = async () => {
  logger.auth('로그아웃 시작')

  // 로그아웃 진행 중 표시 (패스코드 화면 방지)
  safeSessionStorage.set(SESSION_STORAGE_KEYS.IS_LOGGING_OUT, 'true')

  // FCM 토큰 서버에서 삭제 (토큰 삭제 전에 실행)
  try {
    await removeFcmToken()
  } catch (error) {
    logger.warn('FCM 토큰 삭제 실패', error)
  }

  // 네이티브에 로그아웃 요청
  if (window.ReactNativeBridge) {
    try {
      window.ReactNativeBridge.sendMessage('AUTH:LOGOUT_REQUEST', {})
      logger.auth('네이티브에 로그아웃 요청 완료')
    } catch (error) {
      logger.error('네이티브 로그아웃 요청 실패:', error)
    }
  }

  // 메모리 인증 정보 삭제
  useAuthStore.getState().clearAuth()

  // 패스코드 초기화
  try {
    if (window.isWebView && window.webViewBridge) {
      // 웹뷰 환경: 네이티브 패스코드 초기화
      await window.webViewBridge.resetAppPasscode()
    } else {
      // 웹 환경: Zustand store에서 패스코드 삭제
      useAppStore.getState().clearAppPassword()
    }
  } catch (error) {
    logger.warn('패스코드 초기화 실패', error)
  }

  // localStorage 삭제 (필요한 경우)
  localStorage.clear()

  // 로그인 페이지로 리다이렉트
  window.location.href = '/auth/login'
}

/**
 * 인증된 사용자인지 확인 (메모리에서 조회)
 */
export const isAuthenticated = (): boolean => {
  return useAuthStore.getState().isAuthenticated()
}

/**
 * accessToken 확인 (메모리-only 방식)
 * Axios 인터셉터가 자동으로 갱신 처리하므로 단순히 존재 여부만 확인
 */
export const checkTokensOrTryRefresh = async () => {
  const accessToken = useAuthStore.getState().getAccessToken()

  // accessToken이 있으면 통과 (갱신은 Axios 인터셉터가 처리)
  if (accessToken) {
    return true
  }

  // accessToken이 없으면 로그인 페이지로
  window.location.href = '/auth/login'
  return false
}
